import { 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  Target, 
  Zap, 
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  DollarSign,
  Calendar,
  Shield
} from "lucide-react";

// Execution stages mapping
const EXECUTION_STAGES = {
  'lead': { key: 'acquisition', label: 'ACQUISITION', color: '#3B82F6' },
  'new_lead': { key: 'acquisition', label: 'ACQUISITION', color: '#3B82F6' },
  'analyzed': { key: 'underwriting', label: 'UNDERWRITING', color: '#6366F1' },
  'underwritten': { key: 'capital', label: 'CAPITAL', color: '#8B5CF6' },
  'under_contract': { key: 'contract', label: 'CONTRACT', color: '#D4AF37' },
  'contracted': { key: 'contract', label: 'CONTRACT', color: '#D4AF37' },
  // New Processing Stages
  'rehab_estimate': { key: 'rehab', label: 'REHAB ESTIMATE', color: '#F97316' },
  'sent_to_title': { key: 'title', label: 'SENT TO TITLE', color: '#06B6D4' },
  'title_processing': { key: 'title', label: 'TITLE PROCESSING', color: '#14B8A6' },
  'ready_to_close': { key: 'closing', label: 'READY TO CLOSE', color: '#84CC16' },
  'closing': { key: 'closing', label: 'CLOSING', color: '#F59E0B' },
  'closed': { key: 'exit', label: 'CLOSED', color: '#10B981' },
  // Disposition Stages
  'posting_contractors': { key: 'disposition', label: 'POSTING CONTRACTORS', color: '#F43F5E' },
  'contractor_selected': { key: 'disposition', label: 'CONTRACTOR SELECTED', color: '#EC4899' },
  'finding_realtor': { key: 'disposition', label: 'FINDING REALTOR', color: '#D946EF' },
  'listed': { key: 'disposition', label: 'LISTED', color: '#8B5CF6' },
  'under_contract_sale': { key: 'sale', label: 'UNDER CONTRACT', color: '#6366F1' },
  'sold': { key: 'final', label: 'SOLD', color: '#22C55E' },
  'dead': { key: 'dead', label: 'DEAD', color: '#71717A' },
};

export function ExecutionCockpitHeader({ deal, guidance, aiScore }) {
  const stage = EXECUTION_STAGES[deal?.status] || EXECUTION_STAGES[deal?.pipeline_stage] || EXECUTION_STAGES['lead'];
  
  // Calculate metrics - use asking_price OR purchase_price (whichever is available)
  const purchasePrice = deal?.purchase_price || deal?.asking_price || 0;
  const spread = (deal?.arv || 0) - purchasePrice - (deal?.rehab_estimate || 0);
  const capitalRequired = purchasePrice + (deal?.rehab_estimate || 0);
  
  // Calculate AI confidence score based on deal data completeness and metrics
  const calculateAiScore = () => {
    let score = 50; // Base score
    
    // ARV and purchase price present = +15
    if (deal?.arv && purchasePrice) {
      score += 15;
      
      // 70% rule check = +15
      const maxAllowable = deal.arv * 0.7 - (deal.rehab_estimate || 0);
      if (purchasePrice <= maxAllowable) {
        score += 15;
      } else if (purchasePrice / deal.arv <= 0.8) {
        score += 8;
      }
    }
    
    // Rehab estimate present = +5
    if (deal?.rehab_estimate > 0) score += 5;
    
    // Photos present = +5
    if (deal?.photos?.length > 0) score += 5;
    
    // Stage progression bonus
    const stageScores = {
      new_lead: 0, analyzed: 3, underwritten: 6, contracted: 10,
      rehab_estimate: 12, sent_to_title: 14, title_processing: 16,
      ready_to_close: 18, closed: 20, sold: 20
    };
    score += stageScores[deal?.pipeline_stage] || stageScores[deal?.status] || 0;
    
    // Spread quality bonus
    if (spread > 50000) score += 10;
    else if (spread > 30000) score += 5;
    else if (spread < 10000 && spread > 0) score -= 5;
    
    // Risk flags penalty (from guidance)
    const riskCount = guidance?.risk_flags?.length || 0;
    if (riskCount > 0) {
      const criticalRisks = guidance?.risk_flags?.filter(r => r.severity === "critical")?.length || 0;
      score -= criticalRisks * 10;
      score -= (riskCount - criticalRisks) * 3;
    }
    
    return Math.max(0, Math.min(100, Math.round(score)));
  };
  
  // Use passed aiScore or calculate it
  const computedAiScore = aiScore ?? calculateAiScore();
  const riskScore = 100 - computedAiScore;
  const daysActive = Math.floor((Date.now() - new Date(deal?.created_at || Date.now()).getTime()) / (1000 * 60 * 60 * 24));
  
  // Estimate close timeline based on stage
  const getEstCloseTimeline = () => {
    const stageTimelines = {
      'acquisition': '45-60 days',
      'underwriting': '30-45 days',
      'capital': '21-30 days',
      'contract': '14-21 days',
      'closing': '7-14 days',
      'exit': 'Closed'
    };
    return stageTimelines[stage.key] || '—';
  };

  // Get next best action from guidance
  const nextAction = guidance?.next_actions?.[0] || {
    action: 'Analyze this transaction',
    priority: 'medium'
  };

  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-md overflow-hidden mb-6">
      {/* Top Strip - Stage + Key Metrics */}
      <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-white/[0.06]">
        {/* Mobile: Stack layout */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-6">
          <div className="flex items-center gap-3 sm:gap-6">
            {/* Stage Badge */}
            <div 
              className="px-2 sm:px-3 py-1 sm:py-1.5 rounded text-[10px] sm:text-xs font-semibold uppercase tracking-wider"
              style={{ color: stage.color, background: `${stage.color}15`, border: `1px solid ${stage.color}30` }}
            >
              {stage.label}
            </div>
            
            {/* Days Active */}
            <div className="flex items-center gap-1.5 sm:gap-2 text-white/50">
              <Clock className="w-3 sm:w-4 h-3 sm:h-4" />
              <span className="text-xs sm:text-sm font-mono">{daysActive}d active</span>
            </div>
          </div>

          {/* Metrics - wrap on mobile */}
          <div className="flex flex-wrap items-center gap-4 sm:gap-8">
            {/* Confidence Score */}
            <div className="text-left sm:text-right">
              <div className="text-[8px] sm:text-[9px] text-white/40 uppercase tracking-wider mb-0.5 sm:mb-1">AI Confidence</div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Zap className="w-3 sm:w-4 h-3 sm:h-4 text-[#D4AF37]" />
                <span className={`text-base sm:text-xl font-light font-mono ${computedAiScore >= 70 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : computedAiScore >= 50 ? 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' : 'text-red-400'}`}>
                  {computedAiScore}%
                </span>
              </div>
            </div>

          {/* Execution Status */}
          <div className="text-left sm:text-right">
            <div className="text-[8px] sm:text-[9px] text-white/40 uppercase tracking-wider mb-0.5 sm:mb-1">Execution Status</div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              {riskScore < 30 ? (
                <CheckCircle className="w-3 sm:w-4 h-3 sm:h-4 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
              ) : riskScore < 50 ? (
                <Shield className="w-3 sm:w-4 h-3 sm:h-4 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]" />
              ) : (
                <AlertTriangle className="w-3 sm:w-4 h-3 sm:h-4 text-red-400" />
              )}
              <span className={`text-xs sm:text-sm font-medium ${riskScore < 30 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : riskScore < 50 ? 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' : 'text-red-400'}`}>
                {riskScore < 30 ? 'On Track' : riskScore < 50 ? 'Monitor' : 'At Risk'}
              </span>
            </div>
          </div>

          {/* Est. Close - hide on mobile */}
          <div className="hidden sm:block text-right">
            <div className="text-[9px] text-white/40 uppercase tracking-wider mb-1">Est. Close</div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-white/50" />
              <span className="text-sm font-mono text-white/70">{getEstCloseTimeline()}</span>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Main Metrics Row - responsive grid */}
      <div className="px-3 sm:px-6 py-4 sm:py-5 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8">
        {/* Projected Spread */}
        <div>
          <div className="text-[8px] sm:text-[9px] text-white/40 uppercase tracking-wider mb-1 sm:mb-2">Projected Spread</div>
          <div className={`text-xl sm:text-3xl font-light font-mono tracking-tight ${spread > 0 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-red-400'}`}>
            {spread > 0 ? '+' : ''}${Math.abs(spread / 1000).toFixed(0)}K
          </div>
          <div className="hidden sm:flex items-center gap-1 mt-1">
            {spread > 30000 ? (
              <>
                <TrendingUp className="w-3 h-3 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                <span className="text-[10px] text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">Strong margin</span>
              </>
            ) : spread > 15000 ? (
              <>
                <TrendingUp className="w-3 h-3 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]" />
                <span className="text-[10px] text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]">Acceptable</span>
              </>
            ) : (
              <>
                <TrendingDown className="w-3 h-3 text-red-400" />
                <span className="text-[10px] text-red-400">Thin margin</span>
              </>
            )}
          </div>
        </div>

        {/* Capital Required */}
        <div>
          <div className="text-[8px] sm:text-[9px] text-white/40 uppercase tracking-wider mb-1 sm:mb-2">Capital Required</div>
          <div className="text-xl sm:text-3xl font-light font-mono tracking-tight text-white">
            ${(capitalRequired / 1000).toFixed(0)}K
          </div>
          <div className="hidden sm:block text-[10px] text-white/40 mt-1">
            Purchase + Rehab
          </div>
        </div>

        {/* Risk Score */}
        <div>
          <div className="text-[8px] sm:text-[9px] text-white/40 uppercase tracking-wider mb-1 sm:mb-2">Risk Score</div>
          <div className={`text-xl sm:text-3xl font-light font-mono tracking-tight ${riskScore < 30 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : riskScore < 50 ? 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' : 'text-red-400'}`}>
            {riskScore}
          </div>
          <div className="hidden sm:flex items-center gap-1 mt-1">
            <AlertTriangle className={`w-3 h-3 ${riskScore < 30 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : riskScore < 50 ? 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' : 'text-red-400'}`} />
            <span className={`text-[10px] ${riskScore < 30 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : riskScore < 50 ? 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' : 'text-red-400'}`}>
              {riskScore < 30 ? 'Low risk' : riskScore < 50 ? 'Moderate' : 'High risk'}
            </span>
          </div>
        </div>

        {/* ARV */}
        <div>
          <div className="text-[8px] sm:text-[9px] text-white/40 uppercase tracking-wider mb-1 sm:mb-2">After Repair Value</div>
          <div className="text-xl sm:text-3xl font-light font-mono tracking-tight text-white">
            ${((deal?.arv || 0) / 1000).toFixed(0)}K
          </div>
          <div className="hidden sm:block text-[10px] text-white/40 mt-1">
            Target exit value
          </div>
        </div>
      </div>

      {/* Next Best Action Strip - REMOVED - consolidated into DealCommandCenter */}
    </div>
  );
}

export default ExecutionCockpitHeader;
